import { useState } from "react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
    Plus,
    Trash2,
    SquarePen,
} from "lucide-react";
import {
    RuleVersion,
} from "../../shared/schema";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { PartnerSearchDropdown } from "@/widgets/PartnerSearchDropdown";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { useCodeManagerValues, useDeletePartner, useGetPartner } from "@/hooks/useLocalStorage";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { PartnerForm } from "@/shared/partnerschema";
import { AddPartner } from "./AddPartner";


interface TableHierarchicalNodeProps {
    topLevelData: any[];
    pageIndex: string;
    pageSize?: string;
    totalCount: number;
    onPageChange: (code: string) => void;
    onEditForm: (data: any) => void;
}

function TableForm({
    topLevelData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
    onEditForm,
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<PartnerForm[]>([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<PartnerForm | null>(null);

    const deletePortalForm = useDeletePartner();

    React.useEffect(() => {
        if (topLevelData) {
            setCurrentData(topLevelData);
        }
    }, [topLevelData]);

    function handleDeleteClick(e: React.MouseEvent, item: PartnerForm) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete(authCode?: string) {
        if (!itemToDelete) return;

        deletePortalForm.mutate({
            Id: itemToDelete.ID,
        });
    }

    React.useEffect(() => {
        if (deletePortalForm.isSuccess === true) {
            setDeleteDialogOpen(false);
            setReqStatus(deletePortalForm.isSuccess);
            setAlertMessage(`Partner deleted successfully!`);
            setItemToDelete(null);
            setAlertOpen(true);
            deletePortalForm.reset();
        } else if (deletePortalForm.isError) {
            const raw = deletePortalForm.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(deletePortalForm.isSuccess);
            setDeleteDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            deletePortalForm.reset();
        }
    }, [deletePortalForm]);

    return (
        <div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Partner Id</TableHead>
                            <TableHead>Partner Name</TableHead>
                            <TableHead>Partner Category</TableHead>
                            <TableHead>Partner Type</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Total Entry</TableHead>
                            <TableHead>Date/Time Created</TableHead>
                            <TableHead>Modified Date/Time</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={data.ID}>
                                <TableCell className="">{data.ID}</TableCell>
                                <TableCell className="">{data.PartnerName}</TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.PartnerType || "-"}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.PartnerSubName || "-"}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.Description || "-"}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.RecordNumber || "-"}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <div className="flex flex-col">
                                        {data.DateCreated ? (
                                            <>
                                                <span>
                                                    {new Date(data.DateCreated).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.DateCreated).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <div className="flex flex-col">
                                        {data.Modifieddate ? (
                                            <>
                                                <span>
                                                    {new Date(data.Modifieddate).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.Modifieddate).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>

                                <TableCell>
                                    <div className="flex flex-row items-center space-x-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-9 h-9 md:w-10 md:h-10 p-0"
                                                    tooltip="More actions"
                                                >
                                                    <Plus size={20} strokeWidth={2.5} />
                                                    <span className="sr-only">More actions</span>
                                                </Button>
                                            </DropdownMenuTrigger>

                                            <DropdownMenuContent align="end" className="min-w-[180px]">
                                                <DropdownMenuItem
                                                    className="flex items-center px-2 cursor-pointer"
                                                    onClick={() => onEditForm(data)}
                                                >
                                                    <SquarePen size={20} strokeWidth={2.5} className="mr-2" />
                                                    <span className="text-sm">Edit Partner</span>
                                                </DropdownMenuItem>

                                                <DropdownMenuItem
                                                    onClick={(e) => handleDeleteClick(e, data)}
                                                    className="flex items-center px-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                                                >
                                                    <Trash2 size={20} strokeWidth={2.5} className="mr-2" />
                                                    <span className="text-sm">Delete</span>
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={deletePortalForm.isPending}
                itemName={(itemToDelete as PartnerForm)}
                itemType={"Partner"}
                requireAuthCode={false}
            />
        </div>
    );
}


export function PartnerApplicant() {

    const { setBreadcrumbs } = useBreadcrumbs();
    const [topLevelTypes, setTopLevelTypes] = useState<PartnerForm[]>([]);

    const [filterOpen, setFilterOpen] = useState(false);
    const [partnerNameInput, setPartnerNameInput] = useState<string>("");
    const [partnerCategoryInput, setPartnerCategoryInput] = useState<string | undefined>();

    const [partnerName, setPartnerName] = useState<string | undefined>();
    const [partnerTypeInput, setPartnerTypeInput] = useState<string | undefined>("");
    const [partnerType, setPartnerType] = useState<string | undefined>("");
    const [partnerCategory, setPartnerCategory] = useState<string | undefined>();

    // Form dialog states
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);

    // Pagination states
    const [pageIndex, setPageIndex] = useState("1");
    const [pageSize, setPageSize] = useState("10");
    const [totalCount, setTotalCount] = useState(10);

    const { data: types, isLoading } = useGetPartner(
        pageIndex,
        pageSize,
        partnerName,
        partnerCategory,
        partnerType,
    );

    const { data: partnerTypeRaw } = useCodeManagerValues(undefined, "Partner Type");
    const PARTNER_TYPE_TEST_DATA = (partnerTypeRaw ?? [])
        .filter((item: any) => item.entityType === "CODE")
        .map((item: any) => ({ id: String(item.id), label: item.name }));


    // const PARTNER_TYPE_TEST_DATA = [
    //     { id: "1", label: "Bank" },
    //     { id: "2", label: "Insurance" },
    //     { id: "3", label: "Retail" },
    //     { id: "4", label: "Healthcare" },
    // ];

    React.useEffect(() => {
        setBreadcrumbs([
            {
                label: `Partners`,
                path: `/`,
            },
        ]);
    }, []);

    React.useEffect(() => {
        setTopLevelTypes(types?.data.items ?? []);
        setTotalCount(types?.data.total ?? 0);
    }, [types]);

    const handleSearch = () => {
        setPartnerName(partnerNameInput || undefined);
        setPartnerCategory(partnerCategoryInput);
        setPartnerType(partnerTypeInput);
        setPageIndex("1");
    };

    const handleRefresh = (close: boolean) => {
        setPartnerNameInput("");
        setPartnerCategoryInput(undefined);
        setPartnerTypeInput("");

        setPartnerName(undefined);
        setPartnerCategory(undefined);
        setPartnerType("");

        setPageIndex("1");
        if (close) setFilterOpen(false);
    };

    const handleAddForm = () => {
        setEditFormData(null);
        setOpenCreateDialog(true);
    };

    const handleEditForm = (data: any) => {
        setEditFormData(data);
        setOpenCreateDialog(true);
    };

    return (
        <div className="min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-black">
                            Manage Partner
                        </h2>
                        <p className="text-black mt-1 text-sm md:text-base">
                            Create and Manage Partners
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen(true)}
                        >
                            Filter
                        </Button>

                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={handleAddForm}
                        >
                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                            Add Partner
                        </Button>
                    </div>
                </div>
            </div>

            <div>
                {filterOpen && (
                    <Card className="mb-4 md:mb-6">
                        <CardContent className="pt-4">
                            <div className="grid grid-cols-3 gap-4">

                                <PartnerSearchDropdown
                                    value={partnerNameInput}
                                    onChange={(name) => setPartnerNameInput(name)}
                                    placeholder="Partner Name"
                                />

                                <Select
                                    value={partnerCategoryInput}
                                    onValueChange={(value) => setPartnerCategoryInput(value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Partners">Partners</SelectItem>
                                        <SelectItem value="Agencies">Agencies</SelectItem>
                                        <SelectItem value="Department">Department</SelectItem>
                                    </SelectContent>
                                </Select>

                                <Select
                                    value={partnerTypeInput}
                                    onValueChange={(value) => setPartnerTypeInput(value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Partner Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PARTNER_TYPE_TEST_DATA.map((pt) => (
                                            <SelectItem key={pt.id} value={pt.id}>
                                                {pt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                            </div>

                            <div className="mt-4 flex justify-center space-x-2">
                                <Button onClick={handleSearch}>Search</Button>
                                <Button variant="outline" onClick={() => handleRefresh(false)}>
                                    Refresh
                                </Button>
                                <Button variant="outline" onClick={() => handleRefresh(true)}>
                                    Close
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {!isLoading ? (
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <div className="space-y-2">
                            {topLevelTypes && topLevelTypes.length > 0 ? (
                                <TableForm
                                    topLevelData={topLevelTypes}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                    onEditForm={handleEditForm}
                                />
                            ) : (
                                <div className="text-center py-8 text-black">
                                    <p>No Partner found. Create to get started.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="p-6 text-black">Loading...</div>
            )}

            <AddPartner
                open={openCreateDialog}
                onOpenChange={setOpenCreateDialog}
                initialData={editFormData}
            />
        </div>
    );
}